import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { isScoringSupported } from '../data/scoring'
import {
  findGroup,
  findTechnique,
  flattenTechniques,
  getKata,
  navigationTechniques,
  techniqueDisplayName,
  techniqueScriptLang,
  techniqueScriptName,
  usesCategorySelector,
  type KataId,
} from '../data/catalog'
import {
  YoutubePlayer,
  type PlayerStatus,
  type YoutubePlayerHandle,
} from '../lib/youtube/YoutubePlayer'
import { kataBannerBackground } from '../lib/theme/kataBanner'
import { formatTimestamp } from '../lib/youtube/timeFormat'
import {
  getActiveReference,
  getPreferences,
  isYoutubeDataSaverEnabled,
  savePreferences,
} from '../storage/repositories/references'
import { listFavouritesForKata } from '../storage/repositories/personal'
import type { VideoReference } from '../storage/schema'
import { LanguageSwitcher } from '../components/i18n/LanguageSwitcher'
import { NotesOverlay } from '../components/personal/NotesOverlay'
import { YoutubeDataSaverToggle } from '../components/youtube/YoutubeDataSaverToggle'
import { useI18n } from '../i18n'
import styles from './KataPractice.module.css'

export function KataPractice() {
  const { t, locale } = useI18n()
  const { kataId } = useParams<{ kataId: string }>()
  const [searchParams] = useSearchParams()
  const fromPerformance = searchParams.get('fromPerformance')
  const fromElement = searchParams.get('fromElement')
  const techniqueQuery = searchParams.get('technique')
  const kata = kataId ? getKata(kataId as KataId) : undefined
  const scoringEnabled = kata ? isScoringSupported(kata.id) : false
  const returnToJudging = (() => {
    if (!kata || !scoringEnabled || !fromPerformance) return null
    const params = new URLSearchParams({ performance: fromPerformance })
    if (fromElement) params.set('element', fromElement)
    return `/kata/${kata.id}/judge?${params.toString()}`
  })()
  const playerRef = useRef<YoutubePlayerHandle>(null)
  const playbackToken = useRef(0)
  /** Default: stop at technique End. Explicit "Play video" opts into full-video mode. */
  const playbackMode = useRef<'segment' | 'full'>('segment')

  const [reference, setReference] = useState<VideoReference | null | undefined>(undefined)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [playerStatus, setPlayerStatus] = useState<PlayerStatus>('idle')
  const [playerError, setPlayerError] = useState<string | null>(null)
  const [playerReady, setPlayerReady] = useState(false)
  const [favouriteIds, setFavouriteIds] = useState<Set<string>>(new Set())
  const [dataSaverEnabled, setDataSaverEnabled] = useState(true)
  const showCategories = kata ? usesCategorySelector(kata) : false

  // Deep links (e.g. Watch reference) must win over saved lastTechniqueId and must not
  // wait on IndexedDB — otherwise judging briefly/wrongly opens the previous technique.
  useEffect(() => {
    if (!kata || !techniqueQuery) return
    const tech = flattenTechniques(kata).find((t) => t.id === techniqueQuery)
    if (!tech) return
    setSelectedId(tech.id)
    const group = findGroup(kata, tech.id)
    setCategoryId(group?.id ?? null)
  }, [kata, techniqueQuery])

  useEffect(() => {
    if (!kata) return
    let cancelled = false
    setReference(undefined)
    setPlayerReady(false)
    setPlayerError(null)
    ;(async () => {
      try {
        const [ref, prefs, favs] = await Promise.all([
          getActiveReference(kata.id),
          getPreferences(),
          listFavouritesForKata(kata.id),
        ])
        if (cancelled) return
        setReference(ref ?? null)
        setDataSaverEnabled(isYoutubeDataSaverEnabled(prefs))
        setFavouriteIds(new Set(favs.map((f) => f.techniqueId)))
        const techniques = flattenTechniques(kata)
        const queryTech = techniqueQuery
          ? techniques.find((t) => t.id === techniqueQuery)
          : undefined
        if (queryTech) {
          // URL already applied in the sync effect; reinforce after async load.
          setSelectedId(queryTech.id)
          setCategoryId(findGroup(kata, queryTech.id)?.id ?? null)
          return
        }
        const preferred =
          prefs.lastKataId === kata.id && prefs.lastTechniqueId
            ? techniques.find((t) => t.id === prefs.lastTechniqueId)
            : undefined
        const initialTech = preferred ?? techniques[0]
        setSelectedId(initialTech?.id ?? null)
        const group = initialTech ? findGroup(kata, initialTech.id) : kata.groups[0]
        setCategoryId(group?.id ?? null)
      } catch {
        if (cancelled) return
        setLoadError(true)
        setReference(null)
      }
    })()
    return () => {
      cancelled = true
      playbackToken.current += 1
    }
  }, [kata, techniqueQuery])

  const handleDataSaverChange = (enabled: boolean) => {
    setDataSaverEnabled(enabled)
    setPlayerReady(false)
    void savePreferences({ deferYoutubeLoad: enabled })
  }

  useEffect(() => {
    if (!kata || !selectedId) return
    void savePreferences({ lastKataId: kata.id, lastTechniqueId: selectedId })
  }, [kata, selectedId])

  const navList = useMemo(() => {
    if (!kata) return []
    return navigationTechniques(kata, categoryId)
  }, [kata, categoryId])

  const selected = selectedId && kata ? findTechnique(kata, selectedId) : undefined
  const segment = reference?.segments.find((s) => s.techniqueId === selectedId)
  const index = navList.findIndex((t) => t.id === selectedId)
  const prev = index > 0 ? navList[index - 1] : undefined
  const next = index >= 0 && index < navList.length - 1 ? navList[index + 1] : undefined

  const seekDebounceRef = useRef<number | null>(null)

  const cancelPlayback = useCallback(() => {
    playbackToken.current += 1
    playerRef.current?.pause()
  }, [])

  const seekToStartIfConfigured = useCallback(
    (techniqueId: string, ref: VideoReference | null | undefined) => {
      const seg = ref?.segments.find((s) => s.techniqueId === techniqueId)
      if (!seg || !playerRef.current) return
      // Seek-ahead only once the iframe exists; deferred browse only stashes the cue.
      const activated = playerRef.current.isActivated()
      playerRef.current.seekTo(seg.startSeconds, activated)
    },
    [],
  )

  const scheduleSeekToStart = useCallback(
    (techniqueId: string, ref: VideoReference | null | undefined) => {
      if (seekDebounceRef.current !== null) {
        window.clearTimeout(seekDebounceRef.current)
      }
      // Debounce rapid technique flips so we do not thrash buffer requests.
      seekDebounceRef.current = window.setTimeout(() => {
        seekDebounceRef.current = null
        seekToStartIfConfigured(techniqueId, ref)
      }, 180)
    },
    [seekToStartIfConfigured],
  )

  useEffect(() => {
    return () => {
      if (seekDebounceRef.current !== null) {
        window.clearTimeout(seekDebounceRef.current)
      }
    }
  }, [])

  const selectTechnique = (id: string) => {
    cancelPlayback()
    playbackMode.current = 'segment'
    setSelectedId(id)
    if (kata) {
      const group = findGroup(kata, id)
      if (group) setCategoryId(group.id)
    }
    scheduleSeekToStart(id, reference)
  }

  const changeCategory = (nextCategory: string) => {
    if (!kata) return
    cancelPlayback()
    playbackMode.current = 'segment'
    setCategoryId(nextCategory)
    const group = kata.groups.find((g) => g.id === nextCategory)
    const stillValid = group?.techniques.some((t) => t.id === selectedId)
    if (!stillValid) {
      // Changing category clears invalid selection — pick first in new category.
      const first = group?.techniques[0]
      setSelectedId(first?.id ?? null)
      if (first) {
        scheduleSeekToStart(first.id, reference)
      }
    }
  }

  // When player becomes ready, seek to selected segment start (no autoplay).
  useEffect(() => {
    if (!playerReady || !selectedId || !segment) return
    seekToStartIfConfigured(selectedId, reference)
  }, [playerReady, selectedId, segment, reference, seekToStartIfConfigured])

  // Default playback stays within the technique segment (Start–End).
  useEffect(() => {
    if (!segment || playerStatus !== 'playing') return
    if (playbackMode.current !== 'segment') return

    const token = playbackToken.current
    const endAt = segment.endSeconds
    const watch = window.setInterval(() => {
      if (token !== playbackToken.current || playbackMode.current !== 'segment') {
        window.clearInterval(watch)
        return
      }
      const current = playerRef.current?.getCurrentTime() ?? 0
      if (current >= endAt - 0.15) {
        playerRef.current?.pause()
        // Already at the end — do not request unbuffered media past the segment.
        playerRef.current?.seekTo(endAt, false)
        window.clearInterval(watch)
      }
    }, 100)

    return () => window.clearInterval(watch)
  }, [segment, playerStatus, selectedId])

  const playSegment = () => {
    if (!segment) return
    playbackMode.current = 'segment'
    playbackToken.current += 1
    playerRef.current?.seekTo(segment.startSeconds, true)
    playerRef.current?.play()
  }

  const replaySegment = () => {
    if (!segment) return
    cancelPlayback()
    window.setTimeout(() => playSegment(), 0)
  }

  if (!kataId || !kata) {
    return <Navigate to="/" replace />
  }

  const isPlaying = playerStatus === 'playing'
  const togglePlayVideo = () => {
    if (isPlaying) {
      playbackToken.current += 1
      playerRef.current?.pause()
      return
    }
    // Explicit opt-in: watch past End until the user pauses or changes technique.
    playbackMode.current = 'full'
    playbackToken.current += 1
    playerRef.current?.play()
  }
  const readyForControls =
    playerStatus === 'deferred' ||
    playerStatus === 'ready' ||
    playerStatus === 'playing' ||
    playerStatus === 'paused' ||
    playerStatus === 'ended'

  return (
    <div className="app-shell">
      <header className={styles.topBar}>
        <div className={styles.topRow}>
          <Link to="/" className={`btn btn-ghost ${styles.topBack}`}>
            {t('practice.changeKata')}
          </Link>
          <LanguageSwitcher compact className={styles.topLang} />
        </div>
        <h1 className={styles.pageTitle}>
          <span lang="ja">{kata.nameJa}</span>
          <span className={styles.pageRomaji}>{kata.name}</span>
        </h1>
        <div className={styles.topAction}>
          {returnToJudging ? (
            <Link to={returnToJudging} className="btn btn-primary">
              {t('practice.backToJudging')}
            </Link>
          ) : null}
          {scoringEnabled ? (
            <Link
              to={`/kata/${kata.id}/performances`}
              className={returnToJudging ? 'btn btn-secondary' : 'btn btn-primary'}
            >
              {t('practice.judgePerformance')}
            </Link>
          ) : null}
          {reference ? (
            <Link to={`/kata/${kata.id}/setup`} className="btn btn-secondary">
              {t('practice.editVideoAnchors')}
            </Link>
          ) : null}
        </div>
      </header>

      <div
        className={styles.hero}
        style={{
          background: kataBannerBackground(kata.theme),
          color: kata.theme.text,
        }}
      >
        <p className={styles.heroLabel}>{t('practice.label')}</p>
        {selected ? (
          <>
            <p className={styles.heroTitle} lang={techniqueScriptLang(selected, locale)}>
              {techniqueScriptName(selected, locale)}
            </p>
            <p className={styles.heroRomaji}>{selected.name}</p>
          </>
        ) : (
          <p className={styles.heroTitle}>{t('practice.selectTechnique')}</p>
        )}
      </div>

      {loadError && (
        <p className="banner banner-error" role="alert">
          {t('practice.loadError')}
        </p>
      )}

      <div className={styles.workspace}>
        <aside className={`surface ${styles.sidebar}`}>
          {showCategories && (
            <div className={styles.categoryBlock}>
              <label htmlFor="category-select" className={styles.sideTitle}>
                {t('practice.category')}
              </label>
              <select
                id="category-select"
                className={styles.categorySelect}
                value={categoryId ?? ''}
                onChange={(e) => changeCategory(e.target.value)}
              >
                {kata.groups.map((group) => (
                  <option key={group.id} value={group.id} lang={group.nameJa ? 'ja' : undefined}>
                    {group.nameJa ? `${group.nameJa} (${group.name})` : group.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <h2 className={styles.sideTitle} lang={showCategories ? 'ja' : undefined}>
            {showCategories
              ? (() => {
                  const group = kata.groups.find((g) => g.id === categoryId)
                  if (!group) return t('common.techniques')
                  return group.nameJa ? `${group.nameJa} (${group.name})` : group.name
                })()
              : t('common.techniques')}
          </h2>

          {!selectedId && (
            <p className={styles.prompt} role="status">
              {t('practice.selectTechniquePrompt')}
            </p>
          )}

          <ul className={styles.chipList}>
            {navList.map((tech) => {
              const configured = reference?.segments.some((s) => s.techniqueId === tech.id)
              const isSelected = tech.id === selectedId
              return (
                <li key={tech.id}>
                  <button
                    type="button"
                    className={`${styles.chip} ${isSelected ? styles.chipSelected : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => selectTechnique(tech.id)}
                  >
                    <span lang={techniqueScriptLang(tech, locale)}>
                      {favouriteIds.has(tech.id) ? '★ ' : ''}
                      {techniqueScriptName(tech, locale)}
                    </span>
                    <span className={styles.nameRomaji}>{tech.name}</span>
                    {reference && !configured && (
                      <span className={styles.unset}>{t('practice.timingNotSet')}</span>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>

          <div className={styles.navRow}>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!prev}
              onClick={() => prev && selectTechnique(prev.id)}
            >
              {t('common.previous')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!next}
              onClick={() => next && selectTechnique(next.id)}
            >
              {t('common.next')}
            </button>
          </div>
        </aside>

        <section className={`surface ${styles.main}`}>
          {reference === undefined && (
            <p className="banner banner-info" role="status">
              {t('common.loading')}
            </p>
          )}

          {reference === null && (
            <div className={styles.empty}>
              <h2 className={styles.emptyTitle}>{t('practice.addVideoTitle')}</h2>
              <p>
                {scoringEnabled
                  ? t('practice.addVideoBodyScoring', { kata: kata.name })
                  : t('practice.addVideoBody', { kata: kata.name })}
              </p>
              <div className={styles.emptyActions}>
                {scoringEnabled ? (
                  <Link to={`/kata/${kata.id}/performances`} className="btn btn-primary">
                    {t('practice.judgePerformance')}
                  </Link>
                ) : null}
                <Link
                  to={`/kata/${kata.id}/setup`}
                  className={scoringEnabled ? 'btn btn-secondary' : 'btn btn-primary'}
                >
                  {t('practice.addVideo')}
                </Link>
              </div>
            </div>
          )}

          {reference && (
            <>
              <div className={styles.playerHeader}>
                <p className={styles.refTitle}>
                  <span className={styles.techBeside}>
                    {selected?.name ?? t('common.technique')}
                  </span>
                  <span className={styles.refMeta}> · {reference.title}</span>
                </p>
                <Link
                  to={`/kata/${kata.id}/edit${selectedId ? `?technique=${selectedId}` : ''}`}
                  className="btn btn-ghost"
                >
                  {t('practice.editAnchors')}
                </Link>
              </div>

              {playerError && (
                <p className="banner banner-error" role="alert">
                  {playerError}
                </p>
              )}

              <div className={styles.stickyMedia}>
                <YoutubePlayer
                  ref={playerRef}
                  videoId={reference.videoId}
                  title={`${kata.name} — ${selected ? techniqueDisplayName(selected, locale) : reference.title}`}
                  deferLoad={dataSaverEnabled}
                  startSeconds={segment?.startSeconds ?? 0}
                  onReady={() => {
                    setPlayerReady(true)
                  }}
                  onStateChange={setPlayerStatus}
                  onError={setPlayerError}
                />

                {selected && !segment && (
                  <p className="banner banner-info" role="status">
                    {t('practice.timingNotSetFor', {
                      name: techniqueDisplayName(selected, locale),
                    })}
                  </p>
                )}

                {selected && segment && (
                  <p className="banner banner-success" role="status">
                    {t('practice.segment')}{' '}
                    <span className="timestamp">{formatTimestamp(segment.startSeconds)}</span>
                    {' – '}
                    <span className="timestamp">{formatTimestamp(segment.endSeconds)}</span>
                  </p>
                )}

                <div className={styles.playbackBar} role="group" aria-label={t('practice.playbackGroup')}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!segment || !readyForControls}
                    onClick={playSegment}
                  >
                    {isPlaying ? t('practice.playing') : t('practice.playSegment')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={!segment || !readyForControls}
                    onClick={replaySegment}
                  >
                    {t('practice.replay')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={!readyForControls}
                    onClick={togglePlayVideo}
                  >
                    {isPlaying ? t('common.pause') : t('practice.playVideo')}
                  </button>
                </div>
              </div>

              {/* Keep outside stickyMedia so it does not cover .meta while the player sticks. */}
              <YoutubeDataSaverToggle
                enabled={dataSaverEnabled}
                onChange={handleDataSaverChange}
                variant="inline"
              />

              <p className={styles.meta}>
                {t('practice.meta', {
                  count: flattenTechniques(kata).length,
                  videoId: reference.videoId,
                })}
              </p>
            </>
          )}
        </section>

      </div>

      {selected && (
        <NotesOverlay
          kataId={kata.id}
          techniqueId={selected.id}
          techniqueName={techniqueDisplayName(selected, locale)}
          onFavouriteChange={(id, fav) => {
            setFavouriteIds((prev) => {
              const next = new Set(prev)
              if (fav) next.add(id)
              else next.delete(id)
              return next
            })
          }}
        />
      )}

      <p className="disclaimer">{t('practice.disclaimer')}</p>
    </div>
  )
}
